/**
 * Locale-aware formatters for currency and dates.
 *
 * These helpers are intentionally *not* hooks — they take an explicit `locale`
 * so they can be called from non-component code (services, reducers) when
 * needed. From inside a component, pass `useLocale()` as the locale arg:
 *
 *   const locale = useLocale();
 *   formatPrice(item.price, item.currency, locale);
 *
 * Currency stays MXN throughout the marketplace (per CLAUDE.md) — only the
 * number formatting conventions (grouping, decimal mark, currency symbol
 * placement) shift between es-MX and en-US.
 */

import { DEFAULT_LOCALE } from '../hooks/useLocale';

/**
 * Format an amount as currency for the given locale.
 *
 * @param {number} amount
 * @param {string} [currency='MXN']
 * @param {string} [locale=DEFAULT_LOCALE]
 */
export function formatPrice(amount, currency = 'MXN', locale = DEFAULT_LOCALE) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: (currency || 'MXN').toUpperCase(),
  }).format(amount || 0);
}

/**
 * Format an ISO date string (or Date) for the given locale.
 *
 * @param {string|Date} value
 * @param {string} [locale=DEFAULT_LOCALE]
 * @param {Intl.DateTimeFormatOptions} [options={ dateStyle: 'medium' }]
 */
export function formatDate(value, locale = DEFAULT_LOCALE, options = { dateStyle: 'medium' }) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, options);
}

/**
 * Format an ISO date string (or Date) as date + time for the given locale.
 *
 * @param {string|Date} value
 * @param {string} [locale=DEFAULT_LOCALE]
 * @param {Intl.DateTimeFormatOptions} [options={ dateStyle: 'medium', timeStyle: 'short' }]
 */
export function formatDateTime(value, locale = DEFAULT_LOCALE, options = { dateStyle: 'medium', timeStyle: 'short' }) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString(locale, options);
}
